import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import * as Sentry from '@sentry/tanstackstart-react';
import { useEffect, useMemo, useRef } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { onNativeAuthReady } from '#features/api/utils/nativeAuth';
import { useCallStore } from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import { useCurrentSession } from '#features/session-room/hooks/useCurrentSession';
import type { SessionRoomEntry } from '#features/session-room/model/roomSessions';
import { getResizedImageUrl } from '#features/ui/components/Image/loader';
import { avatarFallbackHex } from '#features/ui/utils/avatarFallbackColor';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { mergeRosterVoice } from '#features/voice/utils/mergeRosterVoice';
import {
  nativeCallActivityId,
  onNativeVoiceActivityStatus,
  stopNativeVoiceActivity,
  syncNativeVoiceActivityAuth,
  updateNativeVoiceActivity,
  type NativeActivityStatus,
  type NativeVoiceActivity,
} from '#features/voice/utils/nativeVoiceLiveActivity';
import { envConfig } from '#constants/envConfig';

/** One lifecycle owner above all route surfaces. Native time text runs while WebKit sleeps. */
export const useNativeVoiceLiveActivity = () => {
  const { t } = useLingui();
  const isAuth = useIsAuth();
  const phase = useVoiceStore((s) => s.phase);
  const room = useVoiceStore((s) => s.room);
  const members = useVoiceStore((s) => s.members);
  const micEnabled = useVoiceStore((s) => s.micEnabled);
  const micDenied = useVoiceStore((s) => s.micDenied);
  const callPhase = useCallStore((s) => s.phase);
  const call = useCallStore((s) => s.call);
  const callMedia = useCallStore((s) => s.media);
  const callMicEnabled = useCallStore((s) => s.micEnabled);
  const hasCallMedia = Boolean(
    call && callMedia?.callId === call.callId && callMedia.connectedAt != null
  );
  const { session: currentSession, sessions } = useCurrentSession();
  const retained = useRef<SessionRoomEntry | null>(null);
  const session = sessions?.find((s) => s.sessionId === room?.sessionId);
  if (session) retained.current = session;
  if (!room || retained.current?.sessionId !== room.sessionId) retained.current = session ?? null;
  const inVoice = room != null && phase !== 'idle';
  const entry = inVoice ? (session ?? retained.current) : currentSession;
  const activityId =
    callPhase === 'active' || callPhase === 'outgoing'
      ? callPhase === 'active' && call && hasCallMedia
        ? nativeCallActivityId(call.callId)
        : null
      : inVoice
        ? room.sessionId
        : (entry?.sessionId ?? null);
  const revision = useRef(0);
  const revisionSession = useRef<string | null>(null);
  const lifecycleKey = activityId?.startsWith('call:')
    ? activityId
    : `${activityId}:${inVoice ? 'voice' : 'lobby'}`;
  if (revisionSession.current !== lifecycleKey) {
    revisionSession.current = lifecycleKey;
    revision.current = 0;
  }
  const latest = useRef<NativeVoiceActivity | null>(null);
  const sequence = useRef(Promise.resolve());
  const reportedStatus = useRef<string | null>(null);
  const recordStatus = (result: NativeActivityStatus) => {
    const status = result.activityStatus;
    if (!status) return; // Earlier native shells did not report ActivityKit state.
    const key = `${latest.current?.sessionId ?? ''}:${status}`;
    if (reportedStatus.current === key) return;
    reportedStatus.current = key;
    Sentry.captureMessage(
      status === 'active' ? 'iOS Live Activity started' : 'iOS Live Activity did not start',
      {
        level: status === 'failed' ? 'warning' : 'info',
        tags: { feature: 'voice-live-activity', activityStatus: status },
        // No conversation identifiers, participant names, messages or artwork URLs.
        extra: {
          authorizationEnabled: result.authorizationEnabled,
          payloadBytes: result.payloadBytes,
          errorDomain: result.errorDomain,
          errorCode: result.errorCode,
        },
      }
    );
  };
  const recordStatusRef = useRef(recordStatus);
  recordStatusRef.current = recordStatus;

  useEffect(() => {
    if (!envConfig.IS_NATIVE) return;
    let cancelled = false;
    let dispose: (() => void) | undefined;
    void onNativeVoiceActivityStatus((status) => recordStatusRef.current(status))
      .then((remove) => {
        if (cancelled) remove();
        else dispose = remove;
        return undefined;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) return;
    const sync = () => void syncNativeVoiceActivityAuth().catch(() => {});
    sync();
    return onNativeAuthReady(sync);
  }, [isAuth]);

  const content = useMemo<NativeVoiceActivity | null>(() => {
    if (!isAuth) return null;
    const countPlaceholder = '{count}';
    const totalPlaceholder = '{total}';
    const copy = {
      chat: t({ id: 'voice:activity.chat', message: 'Chat' }),
      unreadMessages: t({
        id: 'voice:activity.unreadMessages',
        message: plural(entry?.unreadCount ?? 0, {
          one: '# unread message',
          other: '# unread messages',
        }),
      }),
      duration: t({ id: 'voice:activity.duration', message: 'Session time' }),
      waiting: t({ id: 'voice:activity.waiting', message: 'Waiting for start' }),
      composition: '',
      reconnecting: t({ id: 'voice:activity.reconnecting', message: 'Reconnecting' }),
      stale: t({ id: 'voice:activity.stale', message: 'Connection not confirmed' }),
      micOn: t({ id: 'voice:activity.micOn', message: 'On' }),
      micOff: t({ id: 'voice:activity.micOff', message: 'Off' }),
      micEnabling: t({ id: 'voice:activity.micEnabling', message: 'Turning on…' }),
      micUnknown: t({ id: 'voice:activity.micUnknown', message: 'Unknown' }),
      open: t({ id: 'voice:activity.open', message: 'Open' }),
      rosterStale: t({ id: 'voice:activity.rosterStale', message: 'Roster not updated' }),
      voiceUnknown: t({ id: 'voice:activity.voiceUnknown', message: 'Voice roster unavailable' }),
      unreadCountTemplate: t({
        id: 'voice:activity.unreadCountTemplate',
        message: `Unread messages: ${countPlaceholder}`,
      }),
      voiceCountTemplate: t({
        id: 'voice:activity.voiceCountTemplate',
        message: `In voice: ${countPlaceholder} of ${totalPlaceholder}`,
      }),
      micDenied: t({ id: 'voice:activity.micDenied', message: 'No microphone access' }),
      micUnavailable: t({ id: 'voice:activity.micUnavailable', message: 'Unavailable' }),
      mute: t({ id: 'voice:activity.mute', message: 'Mute microphone' }),
      unmute: t({ id: 'voice:activity.unmute', message: 'Unmute microphone' }),
      openMic: t({ id: 'voice:activity.openMic', message: 'Open app to control microphone' }),
      joinVoice: t`post:voice.room.join`,
      openSession: t`post:session.room.openSession`,
      sessionEnded: t`post:session.room.phaseEnded`,
      platformSession: t({ id: 'voice:activity.game', message: 'Gaming session' }),
      noVoiceSession: t`post:voice.none.title`,
      inGameSession: t`post:session.room.voiceInGame`,
      discordSession: t`post:voice.discord.title`,
    };
    if (callPhase === 'active' || callPhase === 'outgoing') {
      if (callPhase !== 'active' || !call || !hasCallMedia || !callMedia) return null;
      const connected = callMedia.connectionState === 'connected' && callMedia.peerConnected;
      return {
        kind: 'call',
        sessionId: nativeCallActivityId(call.callId),
        chatId: call.chatId,
        unreadCount: 0,
        avatarURL: toCallAvatarUrl(call.peer.avatar),
        gameName:
          call.peer.displayName?.trim() || t({ id: 'call:activity.peer', message: 'Teammate' }),
        sessionName: call.withVideo
          ? t({ id: 'call:activity.video', message: 'Video call' })
          : t({ id: 'call:activity.voice', message: 'Voice call' }),
        startedAt: callMedia.connectedAt! / 1000,
        expiresAt: null,
        phase:
          callMedia.connectionState === 'connected' && !connected
            ? 'reconnecting'
            : callMedia.connectionState,
        muted: !callMicEnabled || callMedia.micDenied,
        canToggle: connected && !callMedia.micDenied,
        micDenied: callMedia.micDenied,
        revision: revision.current,
        participants: [],
        total: 0,
        copy: {
          ...copy,
          duration: t({ id: 'call:activity.duration', message: 'Call time' }),
          composition: callMedia.micDenied
            ? t({ id: 'call:activity.micDenied', message: 'No microphone access' })
            : t({ id: 'call:activity.connected', message: 'Connected' }),
        },
      };
    }
    if (!inVoice && !entry) return null;
    const roster = mergeRosterVoice(
      {
        hostId: entry?.hostId ?? room?.hostId,
        hostName: entry?.hostName ?? (inVoice ? room?.hostName : null),
        hostAvatar: entry?.hostAvatar,
        members: entry?.members
          .filter((m) => m.status === 'ACCEPTED')
          .map((m) => ({ userId: m.memberId, name: m.displayName, avatar: m.avatar })),
      },
      inVoice ? members : []
    );
    const count = roster.connected,
      total = entry ? roster.total : Math.max(roster.total, count);
    const composition = !inVoice
      ? t({
          id: 'voice:activity.roster',
          message: plural(total, { one: '# participant', other: '# participants' }),
        })
      : count > total || !entry
        ? t({ id: 'voice:activity.connected', message: `In voice: ${count}` })
        : t({ id: 'voice:activity.composition', message: `${count} of ${total} in voice` });
    return {
      kind: 'session',
      sessionId: inVoice ? room!.sessionId : entry!.sessionId,
      sessionPostId: entry?.postId ?? room?.postId,
      voiceMode: entry?.voiceMode ?? 'PLATFORM',
      canJoinVoice: !inVoice && entry?.voiceMode === 'PLATFORM',
      chatId: entry?.chatId ?? null,
      unreadCount: Math.max(0, entry?.unreadCount ?? 0),
      gameIconURL: entry?.game?.icon ? getResizedImageUrl(entry.game.icon, 56, {}, 'png') : null,
      gameWallpaperURL: entry?.game?.desktopImage
        ? getResizedImageUrl(entry.game.desktopImage, 720, {}, 'jpeg')
        : null,
      gameName:
        entry?.game?.name ??
        room?.gameName ??
        t({ id: 'voice:activity.game', message: 'Gaming session' }),
      sessionName: inVoice
        ? t({ id: 'voice:activity.session', message: 'Voice session' })
        : entry?.voiceMode === 'NONE'
          ? t`post:voice.none.title`
          : entry?.voiceMode === 'IN_GAME'
            ? t`post:session.room.voiceInGame`
            : entry?.voiceMode === 'DISCORD'
              ? t`post:voice.discord.title`
              : t({ id: 'voice:activity.game', message: 'Gaming session' }),
      startedAt:
        entry?.startedAt && Number.isFinite(Date.parse(entry.startedAt))
          ? Date.parse(entry.startedAt) / 1000
          : null,
      expiresAt:
        entry?.expiredAt && Number.isFinite(Date.parse(entry.expiredAt))
          ? Date.parse(entry.expiredAt) / 1000
          : null,
      phase: inVoice ? phase : 'idle',
      muted: !inVoice || !micEnabled || micDenied,
      canToggle: inVoice && phase === 'connected' && !micDenied,
      micDenied: inVoice && micDenied,
      revision: revision.current,
      total,
      participants: roster.members.slice(0, total > 5 ? 4 : 5).map((m) => ({
        userId: m.userId,
        avatarURL: toCallAvatarUrl(m.avatar),
        initialColor: avatarFallbackHex(m.name, toCallAvatarUrl(m.avatar)),
        initial: [...(m.name?.trim() || '?')][0].toUpperCase(),
        inVoice: m.inVoice,
      })),
      copy: {
        ...copy,
        composition,
      },
    };
  }, [
    isAuth,
    room,
    phase,
    callPhase,
    call,
    callMedia,
    callMicEnabled,
    hasCallMedia,
    entry,
    inVoice,
    members,
    micEnabled,
    micDenied,
    t,
  ]);
  latest.current = content;

  useEffect(() => {
    if (!envConfig.IS_NATIVE) return;
    let cancelled = false;
    const sync = () => {
      // Queue native mutations; a late update must never resurrect a room after stop.
      sequence.current = sequence.current
        .catch(() => {})
        .then(async () => {
          if (cancelled) return undefined;
          const current = latest.current;
          if (!current) {
            await stopNativeVoiceActivity();
            revision.current = 0;
            return undefined;
          }
          const result = await updateNativeVoiceActivity({
            ...current,
            revision: revision.current,
          });
          if (result) recordStatusRef.current(result);
          if (
            !cancelled &&
            result &&
            result.sessionId === latest.current?.sessionId &&
            result.revision >= revision.current
          ) {
            revision.current = result.revision;
            if (current.kind === 'call') {
              const state = useCallStore.getState();
              if (
                state.phase === 'active' &&
                state.call &&
                nativeCallActivityId(state.call.callId) === result.sessionId
              ) {
                state.setMicEnabled(!result.muted);
              }
            } else if (
              current.phase !== 'idle' &&
              useVoiceStore.getState().phase !== 'idle' &&
              result.sessionId === useVoiceStore.getState().room?.sessionId
            ) {
              useVoiceStore.getState().setMicEnabled(!result.muted);
            }
          }
          return undefined;
        })
        .catch((error: unknown) => {
          // The optional surface must not interrupt voice, but its failure must be diagnosable.
          const code =
            error &&
            typeof error === 'object' &&
            'code' in error &&
            typeof error.code === 'string' &&
            /^LIVE_ACTIVITY_[A-Z_]+$/.test(error.code)
              ? error.code
              : 'LIVE_ACTIVITY_BRIDGE_FAILED';
          const key = `${latest.current?.sessionId ?? ''}:${code}`;
          if (reportedStatus.current === key) return;
          reportedStatus.current = key;
          const data =
            error &&
            typeof error === 'object' &&
            'data' in error &&
            error.data &&
            typeof error.data === 'object'
              ? error.data
              : null;
          Sentry.captureMessage('iOS Live Activity bridge failed', {
            level: 'warning',
            tags: { feature: 'voice-live-activity', nativeCode: code },
            extra: {
              errorDomain:
                data && 'errorDomain' in data && typeof data.errorDomain === 'string'
                  ? data.errorDomain
                  : undefined,
              errorCode:
                data && 'errorCode' in data && typeof data.errorCode === 'number'
                  ? data.errorCode
                  : undefined,
            },
          });
        });
    };
    const visible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    sync();
    const timer = setInterval(sync, 30_000);
    document.addEventListener('visibilitychange', visible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [content]);
};
